// Read-only sharing for a project's areas page and for a single Document
// Control sheet.
//
//   <ShareResourceModal type="areas" id={projectId} title="…" open onClose={…} />
//   <ShareResourceModal type="sheet" id={docControlId} title="…" open onClose={…} />
//
// One modal for both because the backend is one route pair (/shares/:type/:id)
// and the two cases the user cares about — "send it to a partner" and
// "send it to someone who has no account" — are the SAME action. The
// picker lists the project roster; an address that is not on it can be
// typed, and the server resolves whichever it turns out to be. That is
// why there is no partner/guest toggle here: it would be a choice the
// person sharing should not have to make.
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Input, Button, Switch, Tooltip, message, Popconfirm } from "antd";
import {
  LinkOutlined, MailOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { T } from "pages/Issue/theme";
import { filterPeopleOption, renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import {
  peoplePickerOptions, flatOptions, emailValue, companyPickValue, expandCompanyPicks, companyOptionLabelWithCount, groupLabel,
} from "components/PeoplePicker/peoplePickerOptions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const auth = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
});

export default function ShareResourceModal({
  type,            // "areas" | "sheet" | "schedule" | "issues" | "tracking"
  id,              // projectId for areas, docControlId for sheet
  projectId,       // roster lookup (same as id when type === "areas")
  title = "",
  open,
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [roster, setRoster] = useState([]);
  // Values are EMAILS, whether picked off the roster or typed, because
  // that is what POST /shares/:type/:id/invite resolves.
  const [picked, setPicked] = useState([]);
  const [guestName, setGuestName] = useState("");

  const rosterProject = projectId || (type === "areas" ? id : "");

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await fetch(`${url}/shares/${type}/${id}`, { headers: auth() });
      const b = await r.json();
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      setShare(b.share);
      setRecipients(b.recipients || []);
    } catch (e) {
      message.error(e.message || "Could not load sharing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, type, id]);

  useEffect(() => {
    if (!open || !rosterProject) return;
    getParticipantsRoster(rosterProject)
      .then((g) => setRoster(Array.isArray(g) ? g : []))
      .catch(() => setRoster([]));
  }, [open, rosterProject]);

  // Everyone on the project, grouped by their company, plus whatever was
  // typed. maxTagCount keeps a long invite list from swallowing the modal.
  // The shared picker's options (components/PeoplePicker, owner
  // 2026-10-15): each partner headed by its trade, its supervisors and
  // users tagged, and a "Whole company" row that picks all of them at once
  // - expanded to their addresses on the spot, so the invite route sees
  // exactly the people it always did.
  const options = useMemo(() => peoplePickerOptions(roster, {
    personValue: emailValue,
    companyValue: companyPickValue,
    companyOptionLabel: companyOptionLabelWithCount,
  }), [roster]);

  const knownEmails = useMemo(
    () => new Set(flatOptions(options).filter((o) => o.person).map((o) => o.value)),
    [options]
  );
  // A typed address nobody on the project owns is the "new guest" case —
  // that is the only time a name is worth asking for, so the field
  // appears only then.
  const strangers = picked.filter((e) => !knownEmails.has(e));
  // Which partner a typed stranger works for. Optional: the server matches
  // the email domain to a partner on its own, and for the issue manager /
  // tracking an unmatched address becomes a partner of its own. Picking
  // one here files them there and their link signs them in.
  const [strangerPartner, setStrangerPartner] = useState("");
  const partnerOptions = useMemo(() => (roster || [])
    .filter((g) => g.role !== "owner" && g.participantId)
    .map((g) => ({ value: String(g.participantId), label: groupLabel(g) || "Partner" })), [roster]);

  const invite = async () => {
    const bad = picked.find((e) => !EMAIL_RE.test(e));
    if (bad) return message.warning(`"${bad}" is not an email address`);
    if (!picked.length) return message.warning("Pick someone, or type an email address");
    setBusy(true);
    try {
      const people = picked.map((email) => ({
        email,
        // The name only applies to a single typed stranger; roster people
        // bring their own, and the server prefers the roster's anyway.
        name: (strangers.length === 1 && strangers[0] === email) ? guestName.trim() : "",
        partnerId: strangers.includes(email) ? strangerPartner : "",
      }));
      const r = await fetch(`${url}/shares/${type}/${id}/invite`, {
        method: "POST", headers: auth(), body: JSON.stringify({ people }),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      setRecipients(b.recipients || []);
      setPicked([]); setGuestName(""); setStrangerPartner("");
      const failed = (b.invited || []).filter((i) => !i.emailed);
      const signedIn = (b.invited || []).filter((i) => i.signedIn).length;
      // Addresses the server would not share this subject with (the
      // issue manager has no read-only form, so a non-roster address
      // cannot be given it at all).
      for (const f of b.failed || []) message.warning(f.err);
      if (failed.length) {
        message.warning(`${b.invited.length} given access — ${failed.length} email(s) could not be sent; copy their link below`);
      } else if (b.invited?.length) {
        message.success(
          signedIn === b.invited.length
            ? `${b.invited.length} partner(s) sent a link that signs them in`
            : signedIn
              ? `${b.invited.length} invitation(s) sent — ${signedIn} sign in, the rest are read-only`
              : `${b.invited.length} read-only invitation(s) sent`
        );
      }
    } catch (e) {
      message.error(e.message || "Could not share");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${url}/shares/${type}/${id}`, {
        method: "PUT", headers: auth(), body: JSON.stringify(body),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      setShare((s) => ({ ...s, ...b.share }));
      setRecipients(b.recipients || []);
      if (okMsg) message.success(okMsg);
    } catch (e) {
      message.error(e.message || "Could not update sharing");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (guestId) => {
    // The toast has to match the dialog: "Access revoked" was the same
    // overstatement for a roster partner, who keeps the project.
    const row = recipients.find((x) => x.guestId === guestId);
    const endsAccess = !row?.signedIn || type === "sheet";
    setBusy(true);
    try {
      const r = await fetch(`${url}/shares/${type}/${id}/recipient/${guestId}`, {
        method: "DELETE", headers: auth(),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      setRecipients(b.recipients || []);
      message.success(endsAccess
        ? "Access revoked"
        : "Removed from the list — they are still on the project roster");
    } catch (e) {
      message.error(e.message || "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      message.success("Link copied");
    } catch (e) {
      message.warning("Could not copy — select the link and copy it manually");
    }
  };

  const what = type === "areas" ? "Levels & Areas page"
    : type === "schedule" ? "schedule"
    : type === "issues" ? "issue manager"
    : type === "tracking" ? "tracking board"
    : "sheet";

  // Subjects with no read-only form: they are shared signed-in or not at
  // all, so there is no anonymous link to hand out and the promise below
  // ("read-only, no account needed") would be a description of something
  // else. Kept in step with SIGNED_IN_ONLY in utils/resourceShare.
  const signedInOnly = type === "issues" || type === "tracking";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      destroyOnClose
      title={<span>Share {what}{title ? <span style={{ color: T.muted, fontWeight: 400 }}> — {title}</span> : null}</span>}
    >
      <p style={{ fontSize: 12.5, color: T.muted, marginTop: -4 }}>
        {signedInOnly ? (
          <>
            Everyone you add gets a link that <strong>signs them in</strong> - no password, no
            sign-up. Someone new is added to their company's partner roster: pick the company
            below, or leave it and BidLight matches their email domain.
          </>
        ) : (
          <>
            Partners get a link that <strong>signs them in</strong> and can work here - no password,
            no sign-up. Someone new who works for a partner is added to that partner's roster:
            pick the company below, or BidLight matches their email domain. Anyone else gets a
            personal, <strong>read-only</strong> link.
          </>
        )}
      </p>

      {/* Invite */}
      <div style={{ display: "flex", gap: 8, marginBottom: strangers.length === 1 ? 8 : 14 }}>
        <Select
          mode="tags"
          style={{ flex: 1 }}
          value={picked}
          onChange={(v) => setPicked(expandCompanyPicks(v, roster)
            .map((x) => String(x).trim().toLowerCase()).filter(Boolean))}
          options={options}
          placeholder="Partners on this project, or type any email address"
          tokenSeparators={[",", ";", " "]}
          filterOption={filterPeopleOption}
          optionRender={renderPeopleOption}
          maxTagCount="responsive"
          maxTagPlaceholder={peopleTagPlaceholder(options)}
          notFoundContent="No match — type a full email to invite them"
          disabled={busy || loading}
        />
        <Button type="primary" icon={<MailOutlined />} onClick={invite}
                loading={busy} disabled={loading || !picked.length}>
          Send
        </Button>
      </div>
      {strangers.length > 0 && partnerOptions.length > 0 && (
        <Select
          style={{ width: "100%", marginBottom: 8 }}
          allowClear
          value={strangerPartner || undefined}
          onChange={(v) => setStrangerPartner(v || "")}
          options={partnerOptions}
          placeholder={`Company ${strangers.length === 1 ? strangers[0] : "these people"} work${strangers.length === 1 ? "s" : ""} for (optional - matched by email domain)`}
          disabled={busy}
        />
      )}
      {strangers.length === 1 && (
        <Input
          style={{ marginBottom: 14 }}
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder={`Name for ${strangers[0]} (optional — shown on the access list)`}
          disabled={busy}
        />
      )}

      {/* The link itself. Nothing to show when the subject is shared
          signed-in only: the general link addresses the read-only page,
          which does not exist for these, so offering it hands out a
          404. Each recipient's own link is in the list below. */}
      {!signedInOnly && (
      <div style={{
        background: T.bg, border: `1px solid ${T.line}`, borderRadius: 6,
        padding: "10px 12px", marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <LinkOutlined style={{ color: T.navy }} />
          <span style={{
            flex: 1, fontSize: 12, fontFamily: "monospace", color: T.text,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {share?.link || "—"}
          </span>
          <Tooltip title="Copy the general link">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copy(share?.link || "")}
                    disabled={!share?.link} />
          </Tooltip>
          <Tooltip title="Invalidate every link already sent and issue a new one. Recipients keep their access and can be re-sent.">
            <Popconfirm title="Every link already sent will stop working. Continue?"
                        onConfirm={() => patch({ regenerate: true }, "New link issued — the old one no longer works")}>
              <Button size="small" icon={<ReloadOutlined />} disabled={busy} />
            </Popconfirm>
          </Tooltip>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: T.text }}>
            <Switch size="small" checked={!!share?.enabled} disabled={busy}
                    onChange={(v) => patch({ enabled: v }, v ? "Sharing on" : "Sharing off — every link now 404s")} />
            Link active
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: T.text }}>
            <Switch size="small" checked={!!share?.requiresGuestInfo} disabled={busy}
                    onChange={(v) => patch({ requiresGuestInfo: v })} />
            <Tooltip title="If the link gets forwarded, whoever opens it has to give a name and email first — so the access list stays truthful.">
              <span>Ask who's opening it</span>
            </Tooltip>
          </label>
        </div>
      </div>
      )}

      {/* Who has access */}
      <div style={{ fontSize: 11, letterSpacing: ".07em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
        Who has access ({recipients.length})
      </div>
      {recipients.length === 0 ? (
        <div style={{ fontSize: 12.5, color: T.muted, padding: "10px 0" }}>
          Nobody yet. Invitations show here with the last time they opened it.
        </div>
      ) : (
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          {recipients.map((r) => (
            <div key={r.guestId} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 0", borderBottom: `1px solid ${T.line}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: T.ink, fontWeight: 500 }}>
                  {r.name}
                  {r.company ? <span style={{ color: T.muted, fontWeight: 400 }}> · {r.company}</span> : null}
                  {/* Which kind of link this person holds. Decided by the
                      roster, so it can change after they were invited. */}
                  <Tooltip title={r.signedIn
                    ? "On the project roster — their link signs them in and they can work here"
                    : "Not on the roster — their link opens a read-only page"}>
                    <span style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 600, padding: "0 5px",
                      borderRadius: 3, lineHeight: "15px", display: "inline-block",
                      color: r.signedIn ? "#237804" : T.muted,
                      background: r.signedIn ? "#f6ffed" : "#fafafa",
                      border: `1px solid ${r.signedIn ? "#b7eb8f" : T.line}`,
                    }}>
                      {r.signedIn ? "signs in" : "read-only"}
                    </span>
                  </Tooltip>
                </div>
                <div style={{ fontSize: 11.5, color: T.muted, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.email}
                  {" · "}
                  {r.accessCount > 0 && r.lastAccessed
                    ? `opened ${moment(r.lastAccessed).fromNow()}`
                    : "not opened yet"}
                </div>
              </div>
              <Tooltip title={r.link
                ? "Copy this person's personal link"
                : "No link yet — they have not been invited to a page they can open"}>
                <Button size="small" icon={<CopyOutlined />} disabled={!r.link}
                        onClick={() => copy(r.link)} />
              </Tooltip>
              {/* What revoking actually DOES depends on which kind of
                  recipient this is, and the dialog used to promise the
                  same thing for all of them.

                  read-only guest  their link is the only way in, so
                                   revoking really does end their access.
                  sheet, signed in the sheet was opened to them BY this
                                   row (utils/docControlAccess reads it),
                                   so it closes with the row.
                  everything else  they open the project as a member of
                                   its roster; this row only records how
                                   they were told. Taking it away leaves
                                   them exactly as able to open the page
                                   as they were, which is worth saying
                                   out loud rather than implying the
                                   opposite. */}
              <Popconfirm
                title={`Remove ${r.email} from this list?`}
                description={!r.signedIn
                  ? "Their link stops working immediately."
                  : type === "sheet"
                    ? "They lose this sheet: their link stops working and it leaves their register."
                    : "Their link record is removed. They are on this project's roster, so they can still open the page — remove them under Participants to change that."}
                okText={r.signedIn && type !== "sheet" ? "Remove from list" : "Revoke"}
                onConfirm={() => revoke(r.guestId)}
              >
                <Tooltip title={r.signedIn && type !== "sheet"
                  ? "Remove from this list (does not remove them from the project)"
                  : "Revoke their access"}>
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={busy} />
                </Tooltip>
              </Popconfirm>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
